/* -----------------Third parties--------------- */
import { Redirect, Stack, useLocalSearchParams, useRouter } from "expo-router";

import { useUser } from "@clerk/clerk-expo";
import { events } from "@constants/tracking";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { OnboardingAppBar } from "@features/auth";
import VerificationLinkExpired from "@features/auth/components/VerificationLinkExpired";
import { useMagicLinkTokens } from "@features/auth/hooks/useMagicLinkTokens";
import { buildType, currentAppVersion } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import useClerkUser from "@hooks/useClerkUser";
import { useDevice } from "@hooks/useDevice";
import { useGlobalStore } from "@hooks/useGlobalStore";
import useClerkAuth from "@memorang/applets/src/auth/hooks/useClerkAuth";
import {
	formatError,
	isSignUpError,
} from "@memorang/applets/src/auth/screens/helpers";
import { baseDevUrl, isDev } from "@memorang/constants";
import { composeMessage } from "@memorang/helpers";
import { Box, Button, ScrollContainer, useToast } from "@memorang/ui";
import { useQuery } from "@tanstack/react-query";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import DocumentTitle from "components/DocumentTitle";
import * as Device from "expo-device";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { openInbox } from "react-native-email-link";
import { Text, TouchableRipple } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const RESEND_EMAIL_TIMEOUT = 60;

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const VerifyRoute = () => {
	const router = useRouter();
	const params = useLocalSearchParams<{ email: string }>();

	const theme = useAppTheme();
	const app = useAppStore((state) => state.app);
	const tenant = useAppStore((state) => state.tenant);
	const baseUrl = useAppStore((state) => state.app.links.baseUrl);
	const appName = app.name;
	const insets = useSafeAreaInsets();
	useClerkUser();
	const { user } = useUser();
	const [resentEmailIn, setResentEmailIn] = useState(RESEND_EMAIL_TIMEOUT);
	const [showContactSupport, setShowContactSupport] = useState(false);
	const { showToast } = useToast();

	// useEffect(() => {
	// 	trackPageView({ type: pageViewTypes.verifyEmail });
	// }, []);
	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (resentEmailIn > 0) {
			timeout = setTimeout(() => {
				setResentEmailIn(resentEmailIn - 1);
			}, 1000);
		}
		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [resentEmailIn]);

	const redirectUrl = `${isDev ? baseDevUrl : baseUrl}/verify`;
	const { authenticate, isAuthInProgress } = useClerkAuth({
		appId: app.id,
		tenantId: tenant.id,
		redirectUrl,
		onEmailLinkVerify: (_) => {
			setShowContactSupport(true);
			showToast({ message: t("auth.verify.emailResent") });
		},
	});

	const emailFromParams = params.email
		? params.email.replace(/\s/g, "+")
		: undefined;

	const { t } = useTranslation();

	const userLogicalId = user?.publicMetadata.logicalId as string;

	const supportEmail = useAppStore((state) => state.supportEmail);

	const { isSignedIn } = useClerkUser();
	const setLastLoggedInEmail = useGlobalStore(
		(state) => state.setLastLoggedInEmail,
	);

	const { deepLinkUrl, authMode, os, status, redeemCode } =
		useMagicLinkTokens();
	const isLinkExpired = status === "expired";

	useEffect(() => {
		if (isSignedIn && userLogicalId) {
			const isSignUp = authMode === "signup";
			trackCustomEvent({
				eventName: isSignUp ? events.userRegistered : events.userLoggedIn,
				userId: userLogicalId,
				email: emailFromParams,
				tenantId: tenant.id,
			});
		}
	}, [isSignedIn, userLogicalId, emailFromParams, tenant.id, authMode]);
	useQuery({
		queryKey: ["verify-email", emailFromParams],
		queryFn: async () => {
			const newUser = await user?.reload();
			return newUser ?? null;
		},
		enabled: !userLogicalId,
		refetchInterval: 500,
	});

	const deviceInfo = `- Device: ${Device.deviceName || Device.DeviceType[Device.deviceType || 0]}
		- Operating system: ${Platform.OS}
		- App version: ${currentAppVersion}
		- Build type: ${buildType}
		- Email: ${emailFromParams}`;

	const handleExpiredLinkSupport = () => {
		composeMessage(
			`${app.name} Magic link expired`,
			`I'm getting a verification link that is expired. My device info is as follows:
				${deviceInfo}`,
			supportEmail,
		);
	};

	const handleComposeMessage = () => {
		const subject = `${app.name} Magic link not received`;
		const emailBody = `Hi there, I am currently having trouble finding my magic link to login to the ${app.name} app. Thank you! My device info is as follows:
		${deviceInfo}
		`;
		composeMessage(subject, emailBody, supportEmail);
	};

	const { isMobile } = useDevice();

	useEffect(() => {
		if (emailFromParams) {
			setLastLoggedInEmail(emailFromParams);
		}
	}, [emailFromParams, setLastLoggedInEmail]);

	const handleNavigation = (back?: boolean) => {
		if (back) {
			router.back();
		}
	};

	const handleOpenDeepLink = useCallback(() => {
		if (deepLinkUrl) {
			window.location.href = deepLinkUrl;
		}
	}, [deepLinkUrl]);

	useEffect(() => {
		if (isWeb && isMobile && (os === "android" || os === "ios")) {
			handleOpenDeepLink();
		}
	}, [handleOpenDeepLink, isMobile, os]);

	const cta = emailFromParams
		? t("auth.verify.openEmail")
		: authMode === "signup"
			? t("auth.verify.signUpCta")
			: t("auth.verify.signInCta");
	const loading = isSignedIn && !userLogicalId ? true : !emailFromParams;
	const disabled = isSignedIn && !userLogicalId ? true : !emailFromParams;

	const disabledResend = disabled || resentEmailIn > 0 || isAuthInProgress;

	const resendMagicLink = async () => {
		try {
			if (!emailFromParams) {
				throw new Error("Email is required");
			}
			await authenticate(emailFromParams);
		} catch (error: unknown) {
			const isSignUp = isSignUpError(error);
			const longMessage = formatError(error);
			captureErrorToSentry(
				new Error(
					`Error during resend ${isSignUp ? "sign-up" : "sign-in"} magic link`,
				),
				{
					error: JSON.stringify(longMessage),
				},
			);
			trackCustomEvent({
				eventName: isSignUp ? "mem_signup_attempted" : "mem_login_attempted",
				email: emailFromParams,
			});
		}
	};

	if (isSignedIn && userLogicalId) {
		const path = redeemCode
			? (`/redeem?redeemCode=${redeemCode}` as const)
			: ("/home" as const);
		return <Redirect href={path} />;
	}

	return (
		<>
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => {
						return (
							<OnboardingAppBar
								title={
									isLinkExpired
										? undefined
										: t("auth.verify.verifyAccountTitle")
								}
								showLogo={!isMobile}
								onBackAction={() => handleNavigation(true)}
							/>
						);
					},
				}}
			/>
			<DocumentTitle
				title={
					isLinkExpired
						? t("auth.verify.verificationLinkExpired")
						: t("auth.verify.verifyAccountTitle")
				}
			/>
			<ScrollContainer maxWidth="xs">
				{isLinkExpired ? (
					<VerificationLinkExpired
						onSupportContact={handleExpiredLinkSupport}
						onLoginRetry={() => router.replace("/")}
					/>
				) : (
					<Box flexGrow={1} height="100%" padding={16}>
						{isWeb && (os === "android" || os === "ios") ? (
							<Box
								flex={1}
								alignItems="center"
								justifyContent="center"
								gap={16}
							>
								<Icon
									name="open-in-app"
									size={120}
									color={theme.colors.primary}
								/>
								<Text variant="titleLarge" style={{ textAlign: "center" }}>
									{t("auth.verify.redirectingToApp", { appName })}
								</Text>

								<Text style={{ textAlign: "center" }}>
									{t("auth.verify.appNotOpen")}
								</Text>
								<Button
									mode="contained"
									onPress={handleOpenDeepLink}
									style={{ width: "100%" }}
								>
									{t("auth.verify.openApp", { appName })}
								</Button>
								<Text style={{ textAlign: "center" }} variant="bodySmall">
									{t("auth.verify.installAppPrompt", { appName })}
								</Text>
							</Box>
						) : (
							<Box gap={16} justifyContent="space-between" flex={1}>
								<Box flex={1} justifyContent="center">
									<Box alignItems="center" justifyContent="center" gap={16}>
										<Icon
											name="email-check-outline"
											size={120}
											color={theme.colors.primary}
										/>

										<Text variant="titleLarge" style={{ textAlign: "center" }}>
											{t("auth.verify.confirmEmail")}
										</Text>

										<CustomMarkdownRenderer
											textAlign="center"
											fontSize={16}
											variant="stem"
											customStyles={{
												text: {
													lineHeight: 24,
												},
											}}
											text={
												deepLinkUrl
													? t(
															authMode === "signup"
																? "auth.verify.signUpBody"
																: "auth.verify.signInBody",
															{
																appName,
															},
														)
													: t("auth.verify.magicLinkEmail", {
															email: emailFromParams,
															appName,
														})
											}
										/>
									</Box>
								</Box>

								<Box
									width="100%"
									gap={16}
									paddingBottom={insets.bottom}
									maxWidth={500}
									alignSelf="center"
								>
									{(!isWeb || authMode) && (
										<Button
											mode="contained"
											onPress={() => openInbox()}
											disabled={disabled}
											loading={loading}
											style={{ width: "100%" }}
										>
											{cta}
										</Button>
									)}
									{!deepLinkUrl && (
										<TouchableRipple
											onPress={
												showContactSupport
													? handleComposeMessage
													: resendMagicLink
											}
											disabled={disabledResend}
											pointerEvents={disabledResend ? "none" : "auto"}
											style={{
												padding: 16,
												alignItems: "center",
											}}
										>
											<Text
												style={{
													color: disabledResend
														? theme.colors.onSurfaceVariant
														: theme.colors.secondary,
													opacity: disabled ? 0.6 : 1,
												}}
											>
												{showContactSupport
													? t("auth.verify.contactSupport")
													: `${t("auth.verify.resendMagicLink")} ${resentEmailIn > 0 ? `(${resentEmailIn})` : ""}`}
											</Text>
										</TouchableRipple>
									)}
								</Box>
							</Box>
						)}
					</Box>
				)}
			</ScrollContainer>
		</>
	);
};

export default VerifyRoute;
